import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import Pagination from "../../ShopServices/Orders/components/Pagination";
import { useDebounce } from "../../../hooks/useDebounce";

import { useCreatives } from "../hooks";
import type { Creative, CreateAdPayload, UpdateAdPayload } from "../../../api/ads";

import { CreativeTable } from "./components/CreativeTable";
import { CreateAdModal } from "./components/CreateAdModal";

import "./Creatives.css";

const CreativesPage: React.FC = () => {
  const navigate = useNavigate();
  const { functions, values, loading } = useCreatives();

  const [searchInput, setSearchInput] = useState(values.params.search || "");
  const debouncedSearch = useDebounce(searchInput, 300);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editCreativeId, setEditCreativeId] = useState<string | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const handleCreateAd = async (payload: CreateAdPayload) => {
    setCreateSubmitting(true);
    setCreateError(null);
    try {
      await functions.createAd(payload);
      setShowCreateModal(false);
    } catch (err: any) {
      setCreateError(err?.message || "Failed to create ad.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleUpdateAd = async (id: string, payload: UpdateAdPayload) => {
    setEditSubmitting(true);
    setEditError(null);
    try {
      await functions.updateAd(id, payload);
      setEditCreativeId(null);
    } catch (err: any) {
      setEditError(err?.message || "Failed to update ad.");
    } finally {
      setEditSubmitting(false);
    }
  };

  useEffect(() => {
    functions.setParams({ search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const pageSize = values.params.pageSize ?? 10;
  const currentPage = values.params.pageNum ?? 1;
  const totalCount = values.totalCount ?? 0;
  const formattedTotal = useMemo(() => totalCount, [totalCount]);

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Ads", onClick: () => navigate("/ads/overview") },
              { label: "Ads", active: true },
            ]}
          />
        </div>

        <div className="mb-4" style={{ padding: "0 15px" }}>
          <h1 className="ads-page-title">Ads</h1>
          <p className="ads-page-subtitle">Total {formattedTotal} Ads</p>
        </div>

        <Row>
          <Col>
            <Card className="ads-page-card">
              <Card.Body className="ads-page-card__body">
                <div className="ads-toolbar">
                  <div className="ads-searchbar">
                    <div className="ads-searchbar__icon">
                      <i className="ph-magnifying-glass" />
                    </div>
                    <input
                      type="text"
                      className="ads-searchbar__input"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search ads"
                      aria-label="Search creatives"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        className="ads-searchbar__clear"
                        onClick={() => setSearchInput("")}
                        aria-label="Clear search"
                      >
                        <i className="ph-x" />
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    className="ads-create-btn"
                    onClick={() => {
                      setCreateError(null);
                      setShowCreateModal(true);
                    }}
                  >
                    <i className="ph-plus" />
                    Create Ad
                  </button>
                </div>

                {loading.listLoading ? (
                  <ShimmerLoader type="table" rows={6} />
                ) : values.creatives.length > 0 ? (
                  <CreativeTable
                    creatives={values.creatives}
                    onEdit={(creative: Creative) => {
                      setEditError(null);
                      setEditCreativeId(creative.id);
                    }}
                    onToggleStatus={(id, targetStatus) => functions.updateCreativeStatus(id, targetStatus)}
                    onViewPerformance={(creative: Creative) =>
                      navigate(`/ads/creatives/${creative.id}/performance`)
                    }
                  />
                ) : (
                  <div className="ads-empty">
                    <p className="ads-empty__text">No ads found.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {!loading.listLoading && totalCount > 0 && values.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={values.totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={(page) => functions.setPageNum(page)}
                onPageSizeChange={(size) => functions.setPageSize(size)}
              />
            )}
          </Col>
        </Row>

        {values.error && (
          <div style={{ padding: "0 15px" }}>
            <Alert variant="danger" dismissible onClose={() => functions.fetchCreatives()}>
              {values.error}
            </Alert>
          </div>
        )}
      </Container>

      <CreateAdModal
        show={showCreateModal || !!editCreativeId}
        onClose={() => {
          setShowCreateModal(false);
          setEditCreativeId(null);
          setCreateError(null);
          setEditError(null);
        }}
        onSubmit={handleCreateAd}
        onUpdate={handleUpdateAd}
        submitting={createSubmitting || editSubmitting}
        error={createError || editError}
        editAdId={editCreativeId ?? undefined}
      />
    </div>
  );
};

export default CreativesPage;
